import { DownloadManagerEvents } from "@/types/DownloadManager/DownloadManager";
import type { INodeDto } from "@/types/INodeDto";
import { GroupingField, TreeNodeType, TreeRow } from "@/types/LCAoM/TreeBuilder";
import { SelectionManagerEvents } from "@/types/SelectionManager";
import { isOPFSSupported } from "@/types/storage/opfsStorage";
import { buildTreeFromOPFS } from "@/workers/treeBuilderClient";
import { SerializableGroupingField, TreeBuildProgress, TreeBuildProgressHandler } from "@/workers/TreeBuilderWorker";
import useAppStore from "@hooks/useAppStore";
import { useSceneTreeVersion } from "@hooks/useSceneTreeVersion";
import useSelectionManager from "@hooks/useSelectionManager";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import * as Comlink from "comlink";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

export interface UseNodesProps {
  apiRef: MutableRefObject<GridApiPremium>;
  treeDataEnabled?: boolean;
  useMainThread?: boolean;
  enableStringInterning?: boolean;
  /** When false, delays tree building until data is ready (prevents stale renders) */
  isDataReady?: boolean;
}

export interface UseNodesResult {
  /** Whether data is being fetched from API */
  isFetching: boolean;
  /** Whether this is a fresh fetch (no data received yet for this model) */
  isFreshFetch: boolean;
  /** Whether tree is being built in worker */
  isTreeBuilding: boolean;
  /** Whether data fetch has completed (even if tree is still building) */
  fetchComplete: boolean;
  /** Current grouping fields */
  groupingFields: GroupingField[];
  /** Update the grouping fields and rebuild the tree */
  setGroupingFields: (fields: GroupingField[]) => void;
  /** The kind of Co2MaterialTypeSpec for the model */
  co2MaterialTypeSpecKind: string | null;
  /** Tree building progress from worker */
  treeBuildProgress: TreeBuildProgress | null;
  /** Trigger a tree rebuild (e.g., when supplemental nodes change) */
  rebuildTree: () => Promise<TreeRow[]>;
}

/**
 * Extracts nodes by modelId from tree rows (from OPFS/tree build).
 * Used by useResultsNodes to get static NodeDto[] per model without building a separate tree.
 */
export function getNodesByModelIdFromTreeRows(treeRows: TreeRow[]): Map<string, INodeDto[]> {
  const byModel = new Map<string, INodeDto[]>();
  for (const row of treeRows) {
    if (row.type === TreeNodeType.Group || row.data == null) continue;
    const node = row.data as INodeDto;
    const modelId = node.modelId ?? "";
    const list = byModel.get(modelId) ?? [];
    list.push(node);
    byModel.set(modelId, list);
  }
  return byModel;
}

export interface UseResultsNodesResult {
  /** Nodes per modelId (from OPFS via shared tree build; empty if tree not built yet) */
  nodesByModelId: Map<string, INodeDto[]>;
  /** Whether tree data is available (tree has been built for selected models) */
  hasData: boolean;
}

/**
 * Returns static NodeDto[] per model from OPFS (same source as useAssignNodes tree).
 * No tree building in this hook — derives from sceneManager.treeRows when available.
 * Use for Results/ProjectAnalysis where only flat nodes per model are needed.
 */
export function useResultsNodes(): UseResultsNodesResult {
  const appStore = useAppStore();
  const sceneManager = appStore.sceneManager;
  const sceneTreeVersion = useSceneTreeVersion();
  const nodesByModelId = useMemo(() => getNodesByModelIdFromTreeRows(sceneManager.treeRows as TreeRow[]), [sceneManager.treeRows, sceneTreeVersion]);
  const hasData = sceneManager.treeRows.length > 0;
  return { nodesByModelId, hasData };
}

const useAssignNodes = (props: UseNodesProps): UseNodesResult => {
  const { apiRef, treeDataEnabled = true, useMainThread = false, enableStringInterning = true, isDataReady = true } = props;
  const appStore = useAppStore();
  const sceneTreeVersion = useSceneTreeVersion();
  const selectionManager = useSelectionManager();
  const sceneManager = appStore.sceneManager;

  const co2MaterialTypeSpecKind = useMemo(() => {
    const rows = sceneManager.treeRows as TreeRow[];
    const rowWithCo2 = rows?.find(
      (r) =>
        r.data?.co2MaterialTypeSpec != null &&
        r.data.co2MaterialTypeSpec.trim() !== ""
    );
    if (!rowWithCo2?.data?.co2MaterialTypeSpec) return null;
    try {
      const parsed = JSON.parse(rowWithCo2.data.co2MaterialTypeSpec);
      return parsed?.Kind ?? null;
    } catch {
      return null;
    }
  }, [sceneManager.treeRows, sceneTreeVersion]);

  // Check if OPFS is available
  const useOPFS = useMemo(() => isOPFSSupported(), []);

  // Set the label resolver on the sceneManager to use appStore's resolveGroupLabel
  useEffect(() => {
    sceneManager.setLabelResolver((field: string, value: string | number | undefined) => appStore.resolveGroupLabel(field, value));
  }, [sceneManager, appStore]);

  // Sync tree builder options (preserveGroupingHierarchy) with appStore
  useEffect(() => {
    sceneManager.setTreeBuilderOptions({ preserveHierarchy: appStore.preserveGroupingHierarchy });
  }, [sceneManager, appStore.preserveGroupingHierarchy]);

  // Track if we're currently building in the worker (use state to trigger re-renders)
  const [isTreeBuilding, setIsTreeBuilding] = useState(false);
  // Track tree building progress
  const [treeBuildProgress, setTreeBuildProgress] = useState<TreeBuildProgress | null>(null);

  // Monotonic counter to ignore out-of-order tree results (latest write wins)
  const rebuildSeqRef = useRef(0);
  // Debounce timer for selection-changed rebuilds
  const selectionDebounceRef = useRef<number | null>(null);

  // Create stable progress handler ref that can be passed to worker
  const progressHandlerRef = useRef<TreeBuildProgressHandler | null>(null);
  if (!progressHandlerRef.current) {
    progressHandlerRef.current = (progress: TreeBuildProgress) => {
      setTreeBuildProgress(progress);
    };
  }

  // Use stateVersion as dependency to trigger re-renders when DownloadManager state changes
  const dmState = appStore.downloadManager?.state;
  const dmStateVersion = appStore.downloadManager?.stateVersion;

  // Determine if this is a fresh fetch from server (not from OPFS cache)
  // With multi-model support, check if ANY selected model is being freshly downloaded
  const isFreshFetch = useMemo(() => {
    if (!dmState) return false;
    const scenarioId = selectionManager.selectedScenarioId;
    if (!scenarioId) return false;
    const selectedModelIds = selectionManager.getSelectedModelIds(scenarioId);
    return selectedModelIds.some((id: string) => {
      const perModel = dmState.perModel[id];
      return perModel?.status === 'downloading' && perModel?.phase !== 'cached';
    });
  }, [dmStateVersion, selectionManager.selectedScenarioId, selectionManager.project?.models]);


  // Build label map for the worker (pre-compute common label lookups)
  // This converts the appStore's lookup Maps into a serializable format for the worker
  const labelMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};

    // Build cadModelCategoryId -> label map
    if (appStore.cadModelCategoryLookup.size > 0) {
      map['cadModelCategoryId'] = {};
      appStore.cadModelCategoryLookup.forEach((category, id) => {
        if (category.label) {
          map['cadModelCategoryId'][id] = category.label;
        }
      });
    }

    // Build scopeId -> label map
    if (appStore.scopeLookup.size > 0) {
      map['scopeId'] = {};
      appStore.scopeLookup.forEach((scope, id) => {
        if (scope.name) {
          map['scopeId'][String(id)] = scope.name;
        }
      });
    }

    // Build modelId -> label map (same logic as getModelDisplayName)
    if (appStore.modelLookup.size > 0) {
      map['modelId'] = {};
      appStore.modelLookup.forEach((model, id) => {
        const displayName = model.apsItemName ?? model.modelName ?? id;
        map['modelId'][id] = displayName;
      });
    }

    return map;
  }, [
    appStore.cadModelCategoryLookup.size,
    appStore.scopeLookup.size,
    appStore.modelLookup.size
  ]);

  // Update the grid whenever the tree version changes (only when data is ready)
  useEffect(() => {
    // Skip grid updates until data (materials/supplementals) is ready
    if (!isDataReady) {
      console.log('[useAssignNodes] Skipping grid update - data not ready');
      return;
    }
    // Push centrally built rows into the grid
    const rows = sceneManager.treeRows as TreeRow[];
    if (rows && rows.length >= 0) {
      safeUpdateGridRows(apiRef, rows, treeDataEnabled);
    }
  }, [sceneTreeVersion, apiRef, treeDataEnabled, isDataReady]);

  // Helper to rebuild tree (uses worker when MemoryPack enabled)
  // Note: Tree building reads from OPFS (nodes), not from materials/supplementals.
  // The isDataReady check is only applied to grid updates, not tree building.
  const doRebuildTree = useCallback(async () => {
    // Increment sequence; capture local sequence for this invocation
    const mySeq = ++rebuildSeqRef.current;
    if (appStore.isMemoryPackEnabled) {
      setIsTreeBuilding(true);
      setTreeBuildProgress(null); // Reset progress
      try {
        // Use OPFS when available
        if (useOPFS) {
          // Determine which modelIds to build from the active selection
          let idsToBuild: string[] = [];
          try {
            const scenarioId = selectionManager.selectedScenarioId;
            const selectedModelIds = scenarioId ? selectionManager.getSelectedModelIds(scenarioId) : [];
            if (selectedModelIds && selectedModelIds.length > 0) {
              // Keep only scenario models and preserve explicit modelId selection.
              // This avoids accidental cross-version inclusion when multiple models share apsViewId.
              const scenarioModelIdSet = new Set(selectionManager.getScenarioModelIds(scenarioId));
              idsToBuild = selectedModelIds.filter((id: string) => scenarioModelIdSet.has(id));
            }
          } catch { /* ignore lookup errors */ }

          console.log(`[useAssignNodes] Rebuilding tree from OPFS for modelIds=${idsToBuild.join(",") || "(none)"}`);
          if (idsToBuild.length === 0) {
            sceneManager.setTreeRows([]);
            return [] as TreeRow[];
          }
          const delayMs = appStore.gridLoadingTasksDelay;
          const treeBuildResult = await buildTreeFromOPFS(
            idsToBuild,
            toSerializableFields(sceneManager.groupingFields),
            labelMap,
            Comlink.proxy(progressHandlerRef.current!),
            {
              slowTestMode: delayMs > 0,
              slowTestDelayMs: delayMs,
              useMainThread,
              enableStringInterning,
            },
            selectionManager.selectedScenarioId ?? undefined // Pass scenarioId to load supplemental nodes from IndexedDB
          );
          // Only apply if still the latest rebuild
          if (mySeq === rebuildSeqRef.current) {
            sceneManager.setTreeRows(treeBuildResult.rows as TreeRow[]);
            return treeBuildResult.rows as TreeRow[];
          } else {
            // Stale result; ignore
            return sceneManager.treeRows as TreeRow[];
          }
        } else {
          // OPFS not available: main-thread rebuild (Cache path removed)
          const rows = sceneManager.rebuildTree();
          if (mySeq === rebuildSeqRef.current) {
            sceneManager.setTreeRows(rows as TreeRow[]);
            return rows as TreeRow[];
          } else {
            return sceneManager.treeRows as TreeRow[];
          }
        }
      } catch (error) {
        console.error('[useAssignNodes] Worker rebuild failed, falling back to main thread:', error);
        const rows = sceneManager.rebuildTree();
        if (mySeq === rebuildSeqRef.current) {
          sceneManager.setTreeRows(rows as TreeRow[]);
          return rows as TreeRow[];
        } else {
          return sceneManager.treeRows as TreeRow[];
        }
      } finally {
        setIsTreeBuilding(false);
        setTreeBuildProgress(null);
      }
    } else {
      const rows = sceneManager.rebuildTree();
      return rows;
    }
  }, [appStore.isMemoryPackEnabled, sceneManager, labelMap, useOPFS, selectionManager]);

  // Function to update grouping fields
  const setGroupingFields = useCallback((fields: GroupingField[]) => {
    sceneManager.setGroupingFields(fields);
    // doRebuildTree calls sceneManager.setTreeRows which triggers treeVersion change.
    // The treeVersion effect will push rows to grid when isDataReady is true.
    doRebuildTree();
  }, [sceneManager, doRebuildTree]);

  // React to selection changes (checkbox toggles) by rebuilding/pushing rows
  useEffect(() => {
    const onSelectionChanged = () => {
      // Check if ANY downloads are in progress (queued or downloading)
      // This prevents premature tree builds when loading multiple models
      // The drained event will trigger the final build when all are complete
      const dmState = appStore.downloadManager?.state;
      const hasAnyPendingDownloads = dmState && (dmState.inProgress > 0 || 
        Object.values(dmState.perModel).some(pm => pm?.status === 'downloading' || pm?.status === 'queued'));

      if (hasAnyPendingDownloads) {
        console.log('[useAssignNodes] Selection changed - skipping rebuild (downloads in progress)');
        return;
      }

      console.log('[useAssignNodes] Selection changed - rebuilding tree');
      // Debounce to allow selection state to settle (combine multiple rapid toggles)
      if (selectionDebounceRef.current) {
        clearTimeout(selectionDebounceRef.current);
      }
      selectionDebounceRef.current = window.setTimeout(() => {
        // doRebuildTree calls sceneManager.setTreeRows which triggers treeVersion change.
        // The treeVersion effect will push rows to grid when isDataReady is true.
        doRebuildTree();
      }, 60);
    };

    selectionManager.emitter.on(SelectionManagerEvents.SELECTION_CHANGED_EVENT, onSelectionChanged);
    return () => {
      if (selectionDebounceRef.current) {
        clearTimeout(selectionDebounceRef.current);
        selectionDebounceRef.current = null;
      }
      selectionManager.emitter.off(SelectionManagerEvents.SELECTION_CHANGED_EVENT, onSelectionChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doRebuildTree, appStore.downloadManager, selectionManager]);

  // Listen for DownloadManager "drained" event to trigger tree building after downloads complete.
  // If we subscribe after the event already fired (e.g. Assign tab mounted after downloads completed),
  // run rebuild once when already drained so we don't miss it.
  useEffect(() => {
    const onDrained = () => {
      console.log('[useAssignNodes] Downloads drained - building tree with progress tracking');
      doRebuildTree();
    };

    appStore.downloadManager.emitter.on(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, onDrained);

    if (appStore.downloadManager.isDrained()) {
      console.log('[useAssignNodes] Already drained on subscribe - running rebuild once');
      queueMicrotask(() => doRebuildTree());
    }

    return () => appStore.downloadManager.emitter.off(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, onDrained);
  }, [appStore.downloadManager, doRebuildTree]);

  // isFetching: check if ANY selected model is downloading
  const isFetching = useMemo(() => {
    if (!dmState) return false;
    const scenarioId = selectionManager.selectedScenarioId;
    if (!scenarioId) return false;
    const selectedModelIds = selectionManager.getSelectedModelIds(scenarioId);
    return selectedModelIds.some((id: string) => {
      const perModel = dmState.perModel[id];
      return perModel?.status === 'downloading';
    });
  }, [dmStateVersion, selectionManager.selectedScenarioId, selectionManager.project?.models]);

  const fetchComplete = useMemo(() => {
    if (!dmState) return false;
    const scenarioId = selectionManager.selectedScenarioId;
    if (!scenarioId) return false;
    const selectedModelIds = selectionManager.getSelectedModelIds(scenarioId);
    if (selectedModelIds.length === 0) return false;
    return selectedModelIds.every((id: string) => {
      const perModel = dmState.perModel[id];
      return perModel?.status === 'completed';
    });
  }, [dmStateVersion, selectionManager.selectedScenarioId, selectionManager.selectedProject?.models]);

  return {
    isFetching,
    isFreshFetch,
    isTreeBuilding,
    fetchComplete,
    groupingFields: sceneManager.groupingFields,
    setGroupingFields,
    co2MaterialTypeSpecKind,
    treeBuildProgress,
    rebuildTree: doRebuildTree,
  };
};

/**
 * Safely updates the DataGrid rows using flushSync deferred via queueMicrotask.
 * This avoids the React warning about calling flushSync from inside a lifecycle method.
 * 
 * @param apiRef - Reference to the DataGrid API
 * @param rows - Tree rows to update
 * @param onComplete - Optional callback that runs AFTER the grid update completes
 */
function safeUpdateGridRows(
  apiRef: MutableRefObject<GridApiPremium>,
  rows: TreeRow[],
  treeDataEnabled: boolean,
  onComplete?: () => void
): void {
  queueMicrotask(() => {
    ReactDOM.flushSync(() => {
      const nextRows = treeDataEnabled ? rows : rows.filter((r) => r.leaf);
      console.log('[useAssignNodes] safeUpdateGridRows: ', nextRows.length);
      apiRef.current?.setRows?.(nextRows);
      apiRef.current?.unstable_applyFilters?.();
      
      apiRef.current?.autosizeColumns({
        includeOutliers: true,
        includeHeaders: true,
        disableColumnVirtualization: true,
        expand: true,
      });
    });
    // Call onComplete after the grid update has been flushed
    onComplete?.();
  });
}

// No module-level worker; we use the singleton client instead

/**
 * Converts GroupingField[] to SerializableGroupingField[] for passing to the worker.
 * Functions cannot be cloned via postMessage, so we use transform identifiers instead.
 */
function toSerializableFields(fields: GroupingField[]): SerializableGroupingField[] {
  return fields.map(f => {
    const serializable: SerializableGroupingField = {
      field: f.field,
      label: f.label,
    };

    // Map known transform functions to their identifiers
    // The worker will reconstitute the actual function from the identifier
    if (f.transform && f.field === 'cadFamily') {
      serializable.transformId = 'cadFamilyDedup';
    }

    return serializable;
  });
}

export default useAssignNodes;
export { useAssignNodes };
